import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import QuickStartGuide from "../components/QuickStartGuide";
import GuidancePanel from "../components/GuidancePanel";
import InfoBubble from "../components/InfoBubble";
import SupportChoiceBar from "../components/SupportChoiceBar";
import PredictiveWidgets from "../components/PredictiveWidgets";
import GuidedToursPanel from "../components/GuidedToursPanel";
import WindTechTile from "../components/WindTechTile";
import SectorTile from "../components/SectorTile";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle, ClipboardCheck, AlertCircle, Truck,
  TrendingUp, TrendingDown, FileText, Activity,
  MessageSquare, Users, Zap, Bell, ArrowRight,
  BarChart3, Target, CheckCircle2, Clock, Filter,
  ChevronDown, ChevronRight, Eye, EyeOff, Gauge,
  Flame, CircleAlert, Trophy, Loader2
} from "lucide-react";

const Dashboard = () => {
  const { user, isAdmin, isAuditor, isClient } = useAuth();
  const { t } = useTranslation(['dashboard', 'common']);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showClientNames, setShowClientNames] = useState(false);
  const [activeSection, setActiveSection] = useState("snap");
  const [guidance, setGuidance] = useState(null);
  // PR_RUN_038 — sector registry for the dashboard tile grid
  const [sectorRegistry, setSectorRegistry] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (clientFilter) params.set("client_id", clientFilter);
      if (industryFilter) params.set("industry", industryFilter);
      if (typeFilter) params.set("item_type", typeFilter);
      const qs = params.toString() ? `?${params.toString()}` : "";

      const fetches = [
        fetch(`${API}/dashboard/stats`, { credentials: "include" }),
        fetch(`${API}/dashboard/activity`, { credentials: "include" }),
        fetch(`${API}/guidance/context?page=/dashboard`, { credentials: "include" }),
      ];
      if (isAdmin) {
        fetches.push(fetch(`${API}/dashboard/workflow${qs}`, { credentials: "include" }));
        fetches.push(fetch(`${API}/analytics/alerts?unread_only=true`, { credentials: "include" }).catch(() => null));
      }

      const results = await Promise.all(fetches);
      if (results[0]?.ok) setStats(await results[0].json());
      if (results[1]?.ok) setActivity(await results[1].json());
      if (results[2]?.ok) setGuidance(await results[2].json());
      if (results[3]?.ok) setWorkflow(await results[3].json());
      if (results[4]?.ok) setAlerts(await results[4].json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [isAdmin, clientFilter, industryFilter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // PR_RUN_038 — fetch sector registry once on mount; it's static metadata
  // so we don't refetch on filter changes.
  useEffect(() => {
    fetch(`${API}/sectors/registry`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j?.sectors) setSectorRegistry(j.sectors); })
      .catch(() => { /* non-fatal — tile grid degrades gracefully */ });
  }, []);

  const priorityColors = {
    critical: "text-red-400 bg-red-400/10 border-red-400/30",
    high: "text-amber-400 bg-amber-400/10 border-amber-400/30",
    medium: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30",
    minor: "text-slate-400 bg-slate-400/10 border-slate-400/30",
    low: "text-slate-400 bg-slate-400/10 border-slate-400/30",
  };

  const typeColors = {
    CAPA: "text-red-400", Audit: "text-cyan-400", Deviation: "text-amber-400",
  };

  if (loading) {
    return (
      <QMSLayout title={t("dashboard:title")}>
        <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  const snap = workflow?.snap;
  const sectors = workflow?.sector_distribution || {};
  const clients = workflow?.clients || [];

  // Non-admin gets the standard view
  if (!isAdmin) {
    return (
      <QMSLayout title={t('dashboard:title')}>
        <QuickStartGuide />
        {guidance?.info_hint && <InfoBubble text={guidance.info_hint} title={t('dashboard:admin.aboutDashboard')} />}
        {guidance?.next_steps && <GuidancePanel steps={guidance.next_steps} title={isAuditor ? t('dashboard:admin.yourActionItems') : t('dashboard:admin.gettingStarted')} />}
        <div className="mb-6" data-testid="welcome-section">
          <h2 className="font-['Syne'] text-2xl font-bold text-white mb-1">
            {t('dashboard:admin.welcomeBack', { name: user?.name?.split(" ")[0] || "User" })}
          </h2>
          <p className="text-[#8B95A5] text-sm">{isAuditor ? t('dashboard:admin.viewingAssigned') : t('dashboard:admin.viewingDashboard')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-section">
          <KPICard label={t('dashboard:admin.openCAPAs')} value={stats?.capa?.open || 0} total={stats?.capa?.total || 0} icon={AlertTriangle} color="#F87171" />
          <KPICard label={t('dashboard:admin.auditsScheduled')} value={stats?.audits?.scheduled || 0} total={stats?.audits?.total || 0} icon={ClipboardCheck} color="#36A9E1" sub={t('dashboard:admin.passRate', { rate: stats?.audits?.pass_rate || 0 })} />
          <KPICard label={t('dashboard:admin.openDeviations')} value={stats?.deviations?.open || 0} total={stats?.deviations?.total || 0} icon={AlertCircle} color="#FBBF24" />
          <KPICard label={t('dashboard:admin.suppliers')} value={stats?.suppliers?.qualified || 0} total={stats?.suppliers?.total || 0} icon={Truck} color="#4ADE80" sub={t('dashboard:admin.pending', { count: stats?.suppliers?.pending || 0 })} />
        </div>
        <div className="qms-card" data-testid="activity-list">
          <div className="qms-card-header flex items-center gap-2"><Activity size={16} /> {t('dashboard:admin.recentActivity')}</div>
          <div className="qms-card-body">
            {activity.length === 0 ? <p className="text-[#8B95A5] text-sm">{t('dashboard:admin.noActivity')}</p> : (
              <div className="space-y-1">
                {activity.slice(0, 8).map((item, i) => (
                  <div key={i} className="activity-item">
                    <span className={`activity-dot ${item.type === "capa" ? "bg-[#F87171]" : item.type === "audit" ? "bg-[#36A9E1]" : "bg-[#4ADE80]"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{item.title}</p>
                      <p className="text-[#8B95A5] text-xs">{item.type.toUpperCase()} · {item.status}</p>
                    </div>
                    <span className="badge badge-gray text-xs">{item.id}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {guidance?.support_options && <SupportChoiceBar options={guidance.support_options} />}
      </QMSLayout>
    );
  }

  // ============== ADMIN POWER BI DASHBOARD ==============
  return (
    <QMSLayout title={t('dashboard:title')}>
      <QuickStartGuide />
      {guidance?.info_hint && <InfoBubble text={guidance.info_hint} title={t('dashboard:admin.aboutDashboard')} />}
      {guidance?.next_steps && <GuidancePanel steps={guidance.next_steps} title={t('dashboard:admin.actionItems')} />}
      <div className="space-y-6" data-testid="admin-workflow-dashboard">
        {/* Predictive Intelligence Widgets */}
        {(isAdmin || isAuditor) && <PredictiveWidgets />}
        {/* Guided Tours */}
        {(isAdmin || isAuditor) && <GuidedToursPanel />}
        {/* Header + Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="font-['Syne'] text-2xl font-bold text-white flex items-center gap-2">
              <Gauge className="w-6 h-6 text-[#C9A84C]" />
              {t('dashboard:admin.commandCenter')}
            </h2>
            <p className="text-[#8B95A5] text-sm mt-0.5">{t('dashboard:admin.commandCenterSubtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-filters">
            <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
              className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none"
              data-testid="filter-client"
              aria-label="Filter by client"
            >
              <option value="">{t('dashboard:admin.allClients')}</option>
              {clients.map(c => (<option key={c.project_id} value={c.project_id}>{c.project_id}</option>))}
            </select>
            <select value={industryFilter} onChange={e => setIndustryFilter(e.target.value)}
              className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none"
              data-testid="filter-industry"
              aria-label="Filter by industry"
            >
              <option value="">{t('dashboard:admin.allIndustries')}</option>
              {Object.keys(sectors).map(s => (<option key={s} value={s}>{s}</option>))}
            </select>
            <button onClick={() => setShowClientNames(!showClientNames)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-[rgba(255,255,255,0.07)] bg-[#181B23] text-[#8B95A5] hover:text-white transition-colors"
              data-testid="toggle-client-names"
            >
              {showClientNames ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {showClientNames ? t('dashboard:admin.hideNames') : t('dashboard:admin.showNames')}
            </button>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1 bg-[#181B23] p-1 rounded-xl border border-[rgba(255,255,255,0.05)] w-fit">
          {[
            { id: "snap", label: t('dashboard:admin.snap'), icon: Zap },
            { id: "progress", label: t('dashboard:admin.progress'), icon: TrendingUp },
            { id: "quickwin", label: t('dashboard:admin.quickWin'), icon: Trophy },
            { id: "attention", label: t('dashboard:admin.needsAttention'), icon: Flame },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                activeSection === tab.id
                  ? "bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30"
                  : "text-[#8B95A5] hover:text-white border border-transparent"
              }`}
              data-testid={`section-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
              {tab.id === "attention" && (workflow?.needs_attention?.length || 0) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[9px] rounded-full bg-red-500/20 text-red-400">{workflow.needs_attention.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* ===== SNAP IN-TIME ===== */}
        {activeSection === "snap" && snap && (
          <div className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="snap-kpis">
              <SnapCard label="CAPAs" open={snap.capas.open} progress={snap.capas.in_progress} total={snap.capas.total} color="#F87171" />
              <SnapCard label="Audits" open={snap.audits.scheduled} progress={snap.audits.in_progress} total={snap.audits.total} color="#36A9E1" />
              <SnapCard label="Deviations" open={snap.deviations.open} progress={0} total={snap.deviations.total} color="#FBBF24" />
              <SnapCard label="Assessments" open={snap.assessments.total - snap.assessments.completed} progress={0} total={snap.assessments.total} color="#A78BFA" />
              <SnapCard label="Tasks" open={snap.tasks.overdue} progress={snap.tasks.total - snap.tasks.completed - snap.tasks.overdue} total={snap.tasks.total} color="#4ADE80" />
            </div>

            {/* Sector Modules — PR_RUN_036 (Wind live) + PR_RUN_038 (placeholders) */}
            <div className="space-y-3" data-testid="sector-modules">
              <h3 className="text-[11px] uppercase tracking-widest text-[#8B95A5] flex items-center gap-2">
                Sector Modules
                <span className="text-[#8B95A5]/60">·</span>
                <span className="text-[#8B95A5] normal-case tracking-normal">
                  {sectorRegistry.length || 1} sector{sectorRegistry.length === 1 ? "" : "s"}
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Wind Energy — rich tile with live data */}
                <WindTechTile />
                {/* Roadmap sectors — generic placeholders from the registry */}
                {sectorRegistry
                  .filter((s) => s.slug !== "wind_energy")
                  .map((sector) => (
                    <SectorTile key={sector.slug} sector={sector} />
                  ))}
              </div>
            </div>

            {/* Sector Heatmap */}
            <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-5" data-testid="sector-heatmap">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#C9A84C]" /> {t('dashboard:admin.sectorDistribution')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(sectors).map(([sector, counts]) => (
                  <div key={sector} className="bg-[#111318] border border-[rgba(255,255,255,0.05)] rounded-lg p-3">
                    <p className="text-[#8B95A5] text-xs uppercase mb-2 truncate">{sector}</p>
                    <div className="space-y-1">
                      {Object.entries(counts).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between text-xs">
                          <span className="text-[#8B95A5]">{type}</span>
                          <span className="text-white font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Client Overview */}
            {clients.length > 0 && (
              <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-5" data-testid="client-overview">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#C9A84C]" /> {t('dashboard:admin.clientOverview')}
                </h3>
                <div className="divide-y divide-[rgba(255,255,255,0.05)]">
                  {clients.map(c => (
                    <div key={c.project_id} className="flex items-center justify-between py-2.5">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#C9A84C] font-mono bg-[#C9A84C]/10 px-2 py-0.5 rounded">{c.project_id}</span>
                        {showClientNames && <span className="text-sm text-white">{c.client_name}</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{c.sector}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.status === "active" ? "bg-emerald-400/10 text-emerald-400" : "bg-slate-600/30 text-slate-400"}`}>{c.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Alerts */}
            {alerts.alerts?.length > 0 && (
              <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-5" data-testid="snap-alerts">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Bell className="w-4 h-4 text-red-400" /> Active Alerts
                  {alerts.unread_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">{alerts.unread_count}</span>}
                </h3>
                <div className="space-y-2">
                  {alerts.alerts.slice(0, 5).map((a, i) => (
                    <div key={i} className={`flex items-start gap-2 p-2 rounded ${a.severity === "critical" ? "bg-red-500/5" : a.severity === "high" ? "bg-amber-500/5" : "bg-[#111318]"}`}>
                      <AlertTriangle size={12} className={a.severity === "critical" ? "text-red-400 mt-0.5" : "text-amber-400 mt-0.5"} />
                      <div className="min-w-0">
                        <p className="text-white text-xs font-medium truncate">{a.title}</p>
                        <p className="text-[#8B95A5] text-[10px] truncate">{a.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== PROGRESS ===== */}
        {activeSection === "progress" && (
          <div className="space-y-3" data-testid="progress-section">
            {(workflow?.progress || []).length === 0 ? (
              <EmptyState icon={TrendingUp} text="No items currently in progress" />
            ) : (
              (workflow?.progress || []).map((item, i) => (
                <div key={i} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid={`progress-item-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-mono ${typeColors[item.type] || "text-white"}`}>{item.id}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{item.type}</span>
                      {item.client_id && <span className="text-[10px] font-mono text-[#C9A84C]">{item.client_id}</span>}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${priorityColors[item.priority] || priorityColors.medium}`}>{item.priority}</span>
                  </div>
                  <p className="text-sm text-white mb-2">{item.title}</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-[#111318] rounded-full overflow-hidden">
                      <div className="h-full bg-[#C9A84C] rounded-full transition-all" style={{ width: `${item.progress}%` }} />
                    </div>
                    <span className="text-xs text-[#8B95A5]">{item.progress}%</span>
                  </div>
                  {item.sector && <span className="text-[10px] text-[#8B95A5] mt-1 block">{item.sector}</span>}
                </div>
              ))
            )}
          </div>
        )}

        {/* ===== QUICK-WIN ===== */}
        {activeSection === "quickwin" && (
          <div className="space-y-3" data-testid="quickwin-section">
            {(workflow?.quick_wins || []).length === 0 ? (
              <EmptyState icon={Trophy} text={t('dashboard:admin.noQuickWins')} />
            ) : (
              (workflow?.quick_wins || []).map((item, i) => (
                <div key={i} className="bg-[#181B23] border border-emerald-500/10 rounded-xl p-4 flex items-center gap-4" data-testid={`quickwin-item-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <Trophy className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-mono ${typeColors[item.type] || "text-white"}`}>{item.id}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{item.type}</span>
                      {item.client_id && <span className="text-[10px] font-mono text-[#C9A84C]">{item.client_id}</span>}
                    </div>
                    <p className="text-sm text-white truncate">{item.title}</p>
                    <p className="text-xs text-emerald-400/80 mt-0.5">{item.reason}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#8B95A5] shrink-0" />
                </div>
              ))
            )}
          </div>
        )}

        {/* ===== NEEDS ATTENTION ===== */}
        {activeSection === "attention" && (
          <div className="space-y-3" data-testid="attention-section">
            {(workflow?.needs_attention || []).length === 0 ? (
              <EmptyState icon={Flame} text={t('dashboard:admin.noAttention')} />
            ) : (
              (workflow?.needs_attention || []).map((item, i) => (
                <div key={i} className="bg-[#181B23] border border-red-500/10 rounded-xl p-4 flex items-center gap-4" data-testid={`attention-item-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                    <Flame className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-mono ${typeColors[item.type] || "text-white"}`}>{item.id}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{item.type}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${priorityColors[item.priority] || priorityColors.medium}`}>{item.priority}</span>
                      {item.client_id && <span className="text-[10px] font-mono text-[#C9A84C]">{item.client_id}</span>}
                    </div>
                    <p className="text-sm text-white truncate">{item.title}</p>
                    <p className="text-xs text-red-400/80 mt-0.5">{item.reason}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>
      {guidance?.support_options && <SupportChoiceBar options={guidance.support_options} />}
    </QMSLayout>
  );
};

// ============== Sub-components ==============

const KPICard = ({ label, value, total, icon: Icon, color, sub }) => (
  <div className="kpi-card" style={{ "--kpi-accent": color }}>
    <div className="flex items-start justify-between">
      <div>
        <p className="kpi-label">{label}</p>
        <p className="kpi-value">{value}</p>
      </div>
      <Icon className="opacity-80" style={{ color }} size={22} />
    </div>
    <div className="mt-2 text-[#8B95A5] text-xs">
      {sub || `Total: ${total}`}
    </div>
  </div>
);

const SnapCard = ({ label, open, progress, total, color }) => {
  const { t } = useTranslation('dashboard');
  const pct = total > 0 ? Math.round(((total - open - progress) / total) * 100) : 0;
  return (
    <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid={`snap-card-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[#8B95A5] text-xs font-medium uppercase">{label}</span>
        <span className="text-white font-bold text-lg">{total}</span>
      </div>
      <div className="h-1.5 bg-[#111318] rounded-full overflow-hidden mb-2">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-red-400">{open} {t('admin.open')}</span>
        {progress > 0 && <span className="text-amber-400">{progress} {t('admin.active')}</span>}
        <span className="text-emerald-400">{total - open - progress} {t('admin.done')}</span>
      </div>
    </div>
  );
};

const EmptyState = ({ icon: Icon, text }) => (
  <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-12 text-center">
    <Icon className="w-10 h-10 text-[#8B95A5] opacity-30 mx-auto mb-3" />
    <p className="text-[#8B95A5] text-sm">{text}</p>
  </div>
);

export default Dashboard;
